import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Select, Tabs, Button, Input, message, Spin } from 'antd';
import axios from 'axios';
import CustomBreadcrumb from '../../common/CustomBreadcrumb';
import { BreadCrum } from '../../common';
import CsvUploadDragger from '../Dragger/CsvUploadDragger';
import { DeleteUploadIcon, GalleryImage, VideoPlayer, UploadCsvIcon, ChevronIcon, DeleteFaq } from '../../assets/icon';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeContext } from '../../common/themeContext';
import { productListDropdownHandler } from '../../redux/action/productListDropdownAPI';
import { createMaterialAction, createMaterialHandler } from '../../redux/action/createMaterialSlice';
import Loader from '../Loder/loader';
import { API_URL } from '../../services/http';
import { getItem } from '../../common/localStorage';
import './AddMaterial.css';

const { TabPane } = Tabs;
const { TextArea } = Input;
const PRODUCT_PAGE_SIZE = 10;
const FILE_UPLOAD_PATH = 'campaign/material';

const FILE_TYPE_MAP = {
    image: 'IMAGE',
    video: 'VIDEO',
    pdf: 'PDF'
};

const normalizeLanguage = (language) => String(language || 'English').toUpperCase();
const LANGUAGE_OPTIONS = ['English', 'Spanish'];
const hasFaqContent = (faqs = []) =>
    Array.isArray(faqs) && faqs.some((faq) => faq?.question?.trim() || faq?.answer?.trim());

const AddMaterial = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const dispatch = useDispatch();
    const { brandId } = useContext(ThemeContext);
    const productListState = useSelector((state) => state.productListDropdown);
    const { isLoading: saveMaterialLoading, message: saveMaterialMessage, error: saveMaterialError, data: saveMaterialData } = useSelector((state) => state.createMaterial);
    const [productOptions, setProductOptions] = useState([]);
    const [productPage, setProductPage] = useState(1);
    const [hasMoreProducts, setHasMoreProducts] = useState(true);
    const [productSearchText, setProductSearchText] = useState("");
    const isEditMode = Boolean(state?.isEditMode || state?.id || state?.materialId);
    const materialId = state?.id ?? state?.materialId;

    const fetchProducts = useCallback((page = 1, searchText) => {
        if (brandId) {
            const trimmedSearchText = typeof searchText === 'string' ? searchText.trim() : undefined;
            dispatch(
                productListDropdownHandler({
                    offSet: page,
                    limit: PRODUCT_PAGE_SIZE,
                    refBrandId: brandId,
                    ...(trimmedSearchText
                        ? { search: trimmedSearchText, searchString: trimmedSearchText }
                        : {}),
                })
            );
        }
    }, [dispatch, brandId]);

    useEffect(() => {
        setProductOptions([]);
        setProductPage(1);
        setHasMoreProducts(true);
        fetchProducts(1, undefined);
    }, [brandId, fetchProducts]);

    useEffect(() => {
        const responseData = productListState?.data;
        if (!responseData) return;

        const items = (responseData.data || []).map(({ id, name, code }) => ({
            value: String(id),
            label: `${name} - ${code}`,
        }));

        setProductOptions((prev) => {
            const existingKeys = new Set(prev.map((o) => o.value));
            const newItems = items.filter((o) => !existingKeys.has(o.value));
            return productPage === 1 ? items : [...prev, ...newItems];
        });

        const { totalElements, pageNumber = 1, pageSize = PRODUCT_PAGE_SIZE } = responseData;
        const itemCount = items.length;
        if (typeof totalElements === 'number' && totalElements > 0) {
            setHasMoreProducts(pageNumber * pageSize < totalElements);
        } else {
            setHasMoreProducts(itemCount >= PRODUCT_PAGE_SIZE);
        }
    }, [productListState?.data, productPage]);

    const handleProductScroll = useCallback((e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollTop + clientHeight >= scrollHeight - 10 && hasMoreProducts && !productListState?.isLoading) {
            const nextPage = productPage + 1;
            setProductPage(nextPage);
            fetchProducts(nextPage, productSearchText);
        }
    }, [hasMoreProducts, productListState?.isLoading, productPage, fetchProducts, productSearchText]);

    const handleProductSearch = useCallback((nextSearchText) => {
        const trimmedVal = String(nextSearchText ?? "").trim();
        setProductSearchText(trimmedVal);

        // Reset the list so results don't mix between different queries
        setProductOptions([]);
        setProductPage(1);
        setHasMoreProducts(true);

        // If cleared, immediately load the default page.
        if (!trimmedVal) {
            fetchProducts(1, undefined);
        }
    }, [fetchProducts]);

    useEffect(() => {
        if (!productSearchText) return;

        const t = setTimeout(() => {
            fetchProducts(1, productSearchText);
        }, 500);

        return () => clearTimeout(t);
    }, [productSearchText, fetchProducts]);

    const formik = useFormik({
        initialValues: {
            product: undefined,
            language: 'English',
            faqs: [{ question: '', answer: '' }]
        },
        validationSchema: Yup.object({
            product: Yup.string().required('Product is required'),
            faqs: Yup.array().of(
                Yup.object({
                    question: Yup.string().required('Question is required'),
                    answer: Yup.string().required('Answer is required')
                })
            )
        }).test(
            'paired-material-language-required',
            function pairedMaterialLanguageRequired(formValues) {
                const normalizeForCompare = (language) => String(language || '').trim().toLowerCase();
                const countByLanguage = (items = [], language) =>
                    items.filter((item) => normalizeForCompare(item?.language) === normalizeForCompare(language)).length;

                const englishImageCount = countByLanguage(images, 'English');
                const spanishImageCount = countByLanguage(images, 'Spanish');
                if ((englishImageCount > 0 && spanishImageCount === 0) || (spanishImageCount > 0 && englishImageCount === 0)) {
                    return this.createError({
                        path: 'languagePairValidation',
                        message: 'Both English and Spanish versions are required for Image before save.',
                    });
                }

                const englishVideoCount = countByLanguage(videos, 'English');
                const spanishVideoCount = countByLanguage(videos, 'Spanish');
                if ((englishVideoCount > 0 && spanishVideoCount === 0) || (spanishVideoCount > 0 && englishVideoCount === 0)) {
                    return this.createError({
                        path: 'languagePairValidation',
                        message: 'Both English and Spanish versions are required for Video before save.',
                    });
                }

                const englishPdfCount = countByLanguage(pdfs, 'English');
                const spanishPdfCount = countByLanguage(pdfs, 'Spanish');
                if ((englishPdfCount > 0 && spanishPdfCount === 0) || (spanishPdfCount > 0 && englishPdfCount === 0)) {
                    return this.createError({
                        path: 'languagePairValidation',
                        message: 'Both English and Spanish versions are required for PDF before save.',
                    });
                }

                const currentLanguage = formValues?.language || selectedLanguage;
                const mergedFaqs = {
                    ...faqsByLanguage,
                    [currentLanguage]: formValues?.faqs || []
                };
                const englishHasFaq = hasFaqContent(mergedFaqs.English);
                const spanishHasFaq = hasFaqContent(mergedFaqs.Spanish);
                if ((englishHasFaq && !spanishHasFaq) || (spanishHasFaq && !englishHasFaq)) {
                    return this.createError({
                        path: 'languagePairValidation',
                        message: 'Both English and Spanish versions are required for FAQs before save.',
                    });
                }

                return true;
            }
        ),
        onSubmit: (values) => {
            const selectedBrandId = brandId;
            const filesPayload = buildFilesPayload();
            const faqsPayload = buildFaqsPayload(values);

            const payload = {
                productId: Number(values.product),
                refBrandId: selectedBrandId,
                files: filesPayload,
                faqs: faqsPayload
            };
            if (isEditMode && materialId) {
                payload.id = Number(materialId);
            }

            dispatch(createMaterialHandler(payload));
        }
    });

    const { values, setFieldValue, handleChange, handleBlur, handleSubmit, errors, submitCount } = formik;

    const [activeTab, setActiveTab] = useState('image');
    const [productOpen, setProductOpen] = useState(false);
    const [manageSwitch, setManageSwitch] = useState(false);
    const [faqsByLanguage, setFaqsByLanguage] = useState({
        English: [{ question: '', answer: '' }],
        Spanish: [{ question: '', answer: '' }]
    });
    const handleLanguageChange = (language) => {
        const currentLanguage = values.language || 'English';
        const currentFaqs = values.faqs || [{ question: '', answer: '' }];
        const nextFaqsByLanguage = {
            ...faqsByLanguage,
            [currentLanguage]: currentFaqs
        };
        setFaqsByLanguage(nextFaqsByLanguage);
        setFieldValue('language', language);
        setFieldValue('faqs', nextFaqsByLanguage[language] || [{ question: '', answer: '' }]);
        setManageSwitch(language === 'Spanish');
    };
    const handleAddFaq = () => {
        setFieldValue('faqs', [...values.faqs, { question: '', answer: '' }]);
    };

    const handleRemoveFaq = (index) => {
        const newFaqs = values.faqs.filter((_, i) => i !== index);
        setFieldValue('faqs', newFaqs);
    };

    const getAcceptedFiles = () => {
        switch (activeTab) {
            case 'image': return '.png, .jpg, .jpeg';
            case 'video': return '.mp4, .mkv';
            case 'pdf': return '.pdf';
            default: return '';
        }
    };

    const getFormatsText = () => {
        switch (activeTab) {
            case 'image': return 'Supported formats : JPG, PNG';
            case 'video': return 'Supported formats : MP4, MKV';
            case 'pdf': return 'Supported formats : PDF';
            default: return '';
        }
    };

    const [images, setImages] = useState([]);
    const [videos, setVideos] = useState([]);
    const [pdfs, setPdfs] = useState([]);
    const selectedLanguage = values.language || 'English';
    const filteredImages = images.filter((file) => file.language === selectedLanguage);
    const filteredVideos = videos.filter((file) => file.language === selectedLanguage);
    const filteredPdfs = pdfs.filter((file) => file.language === selectedLanguage);

    const handleFileSelected = async (file) => {
        const selectedBrandId = brandId;
        const adminId = getItem('adminId');
        const formData = new FormData();
        formData.append('file', file);
        formData.append('id', Number(adminId));
        formData.append('brandId', selectedBrandId);
        formData.append('path', FILE_UPLOAD_PATH);

        let fileUrl = '';
        try {
            const response = await axios.post(`${API_URL}/file/upload`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
            });
            const uploadData = response?.data?.data || response?.data || {};
            fileUrl = uploadData?.preSignedUrl || uploadData?.fileUrl || uploadData?.url || '';
        } catch (uploadError) {
            const uploadErrorMessage = uploadError?.response?.data?.message || 'File upload failed';
            message.error(uploadErrorMessage);
            return false;
        }

        if (!fileUrl) {
            message.error('File URL was not returned by upload API');
            return false;
        }

        const newFile = {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
            name: file.name,
            size: (file.size / 1024 / 1024).toFixed(2) + ' MB',
            fileUrl,
            language: values.language,
            type: activeTab
        };

        if (activeTab === 'image') setImages(prev => [...prev, newFile]);
        if (activeTab === 'video') setVideos(prev => [...prev, newFile]);
        if (activeTab === 'pdf') setPdfs(prev => [...prev, newFile]);
    };

    const handleRemoveFile = (type, fileId) => {
        if (type === 'image') setImages((prev) => prev.filter((file) => file.id !== fileId));
        if (type === 'video') setVideos((prev) => prev.filter((file) => file.id !== fileId));
        if (type === 'pdf') setPdfs((prev) => prev.filter((file) => file.id !== fileId));
    };

    const buildFilesPayload = useCallback(() => {
        const files = [...images, ...videos, ...pdfs];
        const displayOrderByGroup = {};

        return files.map((file) => {
            const fileType = FILE_TYPE_MAP[file.type];
            const language = normalizeLanguage(file.language);
            const key = `${fileType}_${language}`;
            const displayOrder = displayOrderByGroup[key] || 0;
            displayOrderByGroup[key] = displayOrder + 1;
            return {
                fileType,
                language,
                fileName: file.name,
                fileUrl: file.fileUrl,
                fileSize: file.size,
                displayOrder
            };
        });
    }, [images, videos, pdfs]);

    const buildFaqsPayload = useCallback((formValues) => {
        const currentLanguage = formValues?.language || selectedLanguage;
        const mergedFaqs = {
            ...faqsByLanguage,
            [currentLanguage]: formValues?.faqs || []
        };

        return LANGUAGE_OPTIONS.flatMap((language) => {
            const items = mergedFaqs[language] || [];
            return items
                .filter((faq) => faq.question?.trim() || faq.answer?.trim())
                .map((faq, index) => ({
                    language: normalizeLanguage(language),
                    question: faq.question,
                    answer: faq.answer,
                    displayOrder: index
                }));
        });
    }, [faqsByLanguage, selectedLanguage]);

    useEffect(() => {
        if (!state || !isEditMode) return;

        const mappedProductId = state?.productId != null ? String(state.productId) : undefined;
        if (mappedProductId) {
            setFieldValue('product', mappedProductId, false);
            setProductOptions((prev) => {
                if (prev.some((item) => item.value === mappedProductId)) return prev;
                const labelName = state?.productName || 'Product';
                const labelCode = state?.productCode || '';
                return [...prev, { value: mappedProductId, label: `${labelName}${labelCode ? ` - ${labelCode}` : ''}` }];
            });
        }

        const mapFileType = (fileType) => {
            if (fileType === 'IMAGE') return 'image';
            if (fileType === 'VIDEO') return 'video';
            if (fileType === 'PDF') return 'pdf';
            return '';
        };

        const mapFiles = (files = []) => files
            .filter((file) => file?.fileUrl)
            .map((file, index) => ({
                id: String(file?.id ?? `${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`),
                name: file?.fileName || `File ${index + 1}`,
                size: file?.fileSize || '-',
                fileUrl: file?.fileUrl,
                language: file?.language ? `${String(file.language).charAt(0)}${String(file.language).slice(1).toLowerCase()}` : 'English',
                type: mapFileType(file?.fileType)
            }))
            .filter((file) => file.type);

        const allFiles = Array.isArray(state?.files)
            ? mapFiles(state.files)
            : [
                ...mapFiles(state?.images || []),
                ...mapFiles(state?.videos || []),
                ...mapFiles(state?.pdfs || []),
            ];

        setImages(allFiles.filter((file) => file.type === 'image'));
        setVideos(allFiles.filter((file) => file.type === 'video'));
        setPdfs(allFiles.filter((file) => file.type === 'pdf'));

        const normalizedFaqs = Array.isArray(state?.faqs) ? state.faqs : [];
        const englishFaqs = normalizedFaqs
            .filter((faq) => normalizeLanguage(faq?.language) === 'ENGLISH')
            .map((faq) => ({ question: faq?.question || '', answer: faq?.answer || '' }));
        const spanishFaqs = normalizedFaqs
            .filter((faq) => normalizeLanguage(faq?.language) === 'SPANISH')
            .map((faq) => ({ question: faq?.question || '', answer: faq?.answer || '' }));

        const preparedFaqsByLanguage = {
            English: englishFaqs.length ? englishFaqs : [{ question: '', answer: '' }],
            Spanish: spanishFaqs.length ? spanishFaqs : [{ question: '', answer: '' }]
        };

        setFaqsByLanguage(preparedFaqsByLanguage);
        const defaultLanguage = 'English';
        setFieldValue('language', defaultLanguage, false);
        setFieldValue('faqs', preparedFaqsByLanguage[defaultLanguage], false);
        setManageSwitch(false);
    }, [state, isEditMode, setFieldValue]);

    useEffect(() => {
        if (!saveMaterialMessage || !saveMaterialData) return;
        message.success(saveMaterialMessage || (isEditMode ? 'Material updated successfully!' : 'Material saved successfully!'));
        dispatch(createMaterialAction.createMaterialInfoReset());
        navigate('/ai-campaigns/Material');
    }, [saveMaterialMessage, saveMaterialData, dispatch, navigate, isEditMode]);

    useEffect(() => {
        if (!saveMaterialError) return;
        message.error(saveMaterialError);
        dispatch(createMaterialAction.createMaterialInfoReset());
    }, [saveMaterialError, dispatch]);

    if (saveMaterialLoading) {
        return <Loader />;
    }

    return (
        <div className="rightside materialPage">
            <div className="rightcontent">
                <div className="material-header-container">
                    <p>
                        <strong>Materials</strong>
                    </p>
                    <CustomBreadcrumb
                        items={[
                            { text: BreadCrum.HOME, onClick: () => navigate('/') },
                            { text: "AI Campaigns", onClick: () => navigate('/ai-campaigns/campaign-list') },
                            { text: "Material", onClick: () => navigate('/ai-campaigns/Material') },
                            { text: isEditMode ? "Edit Material" : "Add Material", active: true }
                        ]}
                    />
                </div>

                {/* Select Product Card */}
                <div className="usercard mb-30">
                    <div className="campaignDetailsCard">
                        <span className="campaigncardHeader">Select Product</span>
                    </div>
                    <hr className="m-0 manage-campaign-divider" />
                    <div className="campaigncardBody">
                        <div className="form-group customSelectInput mb-0">
                            <label className="manage-campaign-field-label fs-14 mb-2">Product</label>
                            <Select
                                value={values.product}
                                style={{ width: '100%' }}
                                onChange={(value) => setFieldValue('product', value)}
                                className="customSelect w-100"
                                onOpenChange={setProductOpen}
                                suffixIcon={<ChevronIcon isOpen={productOpen} />}
                                options={productOptions}
                                loading={productListState?.isLoading}
                                notFoundContent={productListState?.isLoading ? <Spin size="small" /> : 'No products found'}
                                onPopupScroll={handleProductScroll}
                                showSearch
                                filterOption={false}
                                onSearch={handleProductSearch}
                                optionFilterProp="label"
                                placeholder="Select product"
                            />
                            {errors.product && (
                                <div className="text-danger small mt-2" role="alert">
                                    {errors.product}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Language and Tabs Section */}
                <div className="d-flex justify-content-end mb-3">
                    {/* <div className="language-toggle">
                        <button
                            className={`lang-btn fs-16 ${values.language === 'English' ? 'active' : ''}`}
                            onClick={() => setFieldValue('language', 'English')}
                        >
                            English
                        </button>
                        <button
                            className={`lang-btn fs ${values.language === 'Spanish' ? 'active' : ''}`}
                            onClick={() => setFieldValue('language', 'Spanish')}
                        >
                            Spanish
                        </button>
                    </div> */}
                    <div className="switches-container mb-30">
                        <div className="mempro" onClick={() => handleLanguageChange('English')}>
                            English
                        </div>
                        <div className="mempro" onClick={() => handleLanguageChange('Spanish')}>
                            Spanish
                        </div>
                        <div
                            className={
                                !manageSwitch ? "switch-wrapper" : "switch-wrapper switchside"
                            }
                        >
                            <div className="switch">
                                {!manageSwitch ? <div>English</div> : <div>Spanish</div>}
                            </div>
                        </div>
                    </div>
                </div>

                <div className=" mb-30 overflow-hidden">
                    <Tabs activeKey={activeTab} onChange={setActiveTab} className="material-tabs fs-16">
                        <TabPane tab="Image" key="image">
                            <div className="p-4 bg-white">
                                <CsvUploadDragger
                                    accept={getAcceptedFiles()}
                                    supportedFormatsText={getFormatsText()}
                                    maxSizeMB={5}
                                    uploadText={<span>Click here or drag & drop to upload <b>.png</b> file</span>}
                                    showFileInside={false}
                                    multiple={true}
                                    onFileSelected={handleFileSelected}
                                />
                                {filteredImages.length > 0 && (
                                    <div className="mt-4">
                                        <h3 className="fs-16 fw-bold mb-3 d-flex align-items-center gap-2">
                                            <GalleryImage /> Uploaded Images ({String(filteredImages.length).padStart(2, '0')})
                                        </h3>
                                        <div className="uploaded-files-grid">
                                            {filteredImages.map((file) => (
                                                <div key={file.id} className="uploaded-file-card">
                                                    <div className="d-flex align-items-center gap-3 ">
                                                        <div className="uploadCsvIconContainer">
                                                            <GalleryImage color="white" />
                                                        </div>
                                                        <div>
                                                            <div className="file-name-text fs-14">{file.name}</div>
                                                            <div className="file-size-text">{file.size} ({file.language})</div>
                                                        </div>
                                                    </div>
                                                    <div className="d-flex gap-2">
                                                        <button
                                                            type="button"
                                                            className="cursorPointer deleteCampapign bg-transparent border-0 p-0 "
                                                            onClick={() => handleRemoveFile('image', file.id)}
                                                            aria-label="Remove uploaded file"
                                                        >
                                                            <DeleteUploadIcon />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                    </div>
                                )}
                            </div>
                        </TabPane>
                        <TabPane tab="Video" key="video">
                            <div className="p-4 bg-white">
                                <CsvUploadDragger
                                    accept={getAcceptedFiles()}
                                    supportedFormatsText={getFormatsText()}
                                    maxSizeMB={20}
                                    uploadText={<span>Click here or drag & drop to upload <b>.mp4</b> file</span>}
                                    showFileInside={false}
                                    multiple={true}
                                    onFileSelected={handleFileSelected}
                                />
                                {filteredVideos.length > 0 && (
                                    <div className="mt-4">
                                        <h3 className="fs-16 fw-bold mb-3 d-flex align-items-center gap-2">
                                            <VideoPlayer /> Uploaded Videos ({String(filteredVideos.length).padStart(2, '0')})
                                        </h3>
                                        <div className="uploaded-files-grid">
                                            {filteredVideos.map((file) => (
                                                <div key={file.id} className="uploaded-file-card">
                                                    <div className="d-flex align-items-center gap-3">
                                                        <div className="uploadCsvIconContainer">
                                                            <VideoPlayer color="white" />
                                                        </div>
                                                        <div>
                                                            <div className="file-name-text fs-14">{file.name}</div>
                                                            <div className="file-size-text">{file.size} ({file.language})</div>
                                                        </div>
                                                    </div>
                                                    <div className="d-flex gap-2">
                                                        <button
                                                            type="button"
                                                            className="cursorPointer deleteCampapign bg-transparent border-0 p-0"
                                                            onClick={() => handleRemoveFile('video', file.id)}
                                                            aria-label="Remove uploaded video"
                                                        >
                                                            <DeleteUploadIcon />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </TabPane>
                        <TabPane tab="PDF" key="pdf">
                            <div className="p-4 bg-white">
                                <CsvUploadDragger
                                    accept={getAcceptedFiles()}
                                    supportedFormatsText={getFormatsText()}
                                    maxSizeMB={5}
                                    uploadText={<span>Click here or drag & drop to upload <b>.pdf</b> file</span>}
                                    showFileInside={false}
                                    multiple={true}
                                    onFileSelected={handleFileSelected}
                                />
                                {filteredPdfs.length > 0 && (
                                    <div className="mt-4">
                                        <h3 className="fs-16 fw-bold mb-3 d-flex align-items-center gap-2">
                                            <UploadCsvIcon color="#162C4E" /> Uploaded PDFs ({String(filteredPdfs.length).padStart(2, '0')})
                                        </h3>
                                        <div className="uploaded-files-grid">
                                            {filteredPdfs.map((file) => (
                                                <div key={file.id} className="uploaded-file-card">
                                                    <div className="d-flex align-items-center gap-3">
                                                        <div className="uploadCsvIconContainer">
                                                            <UploadCsvIcon color="white" />
                                                        </div>
                                                        <div>
                                                            <div className="file-name-text fs-14">{file.name}</div>
                                                            <div className="file-size-text">{file.size} ({file.language})</div>
                                                        </div>
                                                    </div>
                                                    <div className="d-flex gap-2">
                                                        <button
                                                            type="button"
                                                            className="cursorPointer deleteCampapign bg-transparent border-0 p-0"
                                                            onClick={() => handleRemoveFile('pdf', file.id)}
                                                            aria-label="Remove uploaded pdf"
                                                        >
                                                            <DeleteUploadIcon />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </TabPane>
                        <TabPane tab="FAQs" key="faqs">
                            <div className="faq-tabs-container p-0">
                                {values.faqs.map((faq, index) => (
                                    <React.Fragment key={index}>
                                        <div className="faq-form-item p-4 position-relative d-flex flex-column gap-4">
                                            <div className="form-group">
                                                <label className="fs-16 fw-bold mb-2">Question</label>
                                                <TextArea
                                                    rows={1}
                                                    name={`faqs[${index}].question`}
                                                    placeholder="Type your Question here"
                                                    value={faq.question}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                />
                                                {errors.faqs?.[index]?.question && (
                                                    <div className="text-danger small mt-1" role="alert">
                                                        {errors.faqs?.[index]?.question}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="form-group mb-0">
                                                <label className="fs-16 fw-bold mb-2">Answer</label>
                                                <TextArea
                                                    rows={4}
                                                    name={`faqs[${index}].answer`}
                                                    placeholder="Type your Answer here"
                                                    value={faq.answer}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                />
                                                {errors.faqs?.[index]?.answer && (
                                                    <div className="text-danger small mt-1" role="alert">
                                                        {errors.faqs?.[index]?.answer}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="d-flex justify-content-end gap-3 mt-2 pr-3">
                                                {values.faqs.length - 1 === index && (
                                                    <button
                                                        type="button"
                                                        onClick={handleAddFaq}
                                                        className="btn-add-another fw-18 d-flex align-items-center gap-2"
                                                    >
                                                        <span className="plus-icon mr-1">+</span>
                                                        <span className="plus-icon-text fw-18">Add Another FAQs</span>
                                                    </button>
                                                )}
                                                {values.faqs.length > 1 && (
                                                    <button
                                                        type="button"
                                                        className="btn-remove-faq-box"
                                                        onClick={() => handleRemoveFaq(index)}
                                                    >
                                                        <DeleteFaq />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        {index !== values.faqs.length - 1 && <div className="faq-separator" />}
                                    </React.Fragment>
                                ))}
                            </div>
                        </TabPane>
                    </Tabs>
                </div>

                {/* Footer Actions */}
                <div className="d-flex justify-content-end gap-3 mt-4 mb-5">
                    {(errors.languagePairValidation) && (
                        <div className="w-100 text-danger small me-auto" style={{ color: 'red' }}>
                            {errors.languagePairValidation}
                        </div>
                    )}
                    <Button className="btn-cancel" onClick={() => navigate(-1)} disabled={saveMaterialLoading}>Cancel</Button>
                    <Button type="primary" className="btn-save" onClick={handleSubmit} loading={saveMaterialLoading} disabled={saveMaterialLoading}>Save</Button>
                </div>
            </div>
        </div>
    );
};

export default AddMaterial;
